import { __decorate } from "tslib";
import React, { Children, cloneElement, createElement, isValidElement, } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { action as mobxAction, computed, isArrayLike, observable, runInAction } from 'mobx';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import defaultTo from 'lodash/defaultTo';
import Responsive, { hasBreakPointMap } from 'choerodon-ui/lib/responsive/Responsive';
import { getConfig, getProPrefixCls } from 'choerodon-ui/lib/configure';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import { getTooltip } from 'choerodon-ui/lib/_util/TooltipUtils';
import isFunction from 'lodash/isFunction';
import axios from '../axios';
import autobind from '../_util/autobind';
import isFragment from '../_util/isFragment';
import { getFieldsById } from '../field/FormField';
import FormContext from './FormContext';
import DataSetComponent from '../data-set/DataSetComponent';
import { FormLayout, LabelAlign, LabelLayout, ResponsiveKeys } from './enum';
import { defaultColumns, defaultExcludeUseColonTag, defaultLabelWidth, FIELD_SUFFIX, getProperty, hasParentElement, normalizeLabelWidth, } from './utils';
import FormVirtualGroup from './FormVirtualGroup';
import { DataSetEvents } from '../data-set/enum';
import Item from './Item';
import FormItemLabel from './FormItemLabel';
/**
 * 表单name生成器
 */
const NameGen = (function* (start) {
    while (true) {
        start += 1;
        yield `form-${start}`;
    }
})(0);
const labelWidthPropTypes = PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf(['auto']),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])])),
]);
const labelAlignPropTypes = PropTypes.oneOf([LabelAlign.left, LabelAlign.center, LabelAlign.right]);
const labelLayoutPropTypes = PropTypes.oneOf([
    LabelLayout.horizontal,
    LabelLayout.vertical,
    LabelLayout.placeholder,
    LabelLayout.float,
    LabelLayout.none,
]);
let Form = class Form extends DataSetComponent {
    constructor(props, context) {
        super(props, context);
        this.fields = [];
        this.name = NameGen.next().value;
        this.validating = false;
        this.prepareForReport = {};
        runInAction(() => {
            this.responsiveItems = [];
        });
    }
    get axios() {
        return this.observableProps.axios || getConfig('axios') || axios;
    }
    get dataSet() {
        const { record } = this;
        if (record) {
            return record.dataSet;
        }
        const { observableProps } = this;
        if ('dataSet' in observableProps) {
            return observableProps.dataSet;
        }
        return observableProps.contextDataSet;
    }
    get record() {
        const { observableProps } = this;
        if ('record' in observableProps) {
            return observableProps.record;
        }
        const { dataSet, dataIndex } = observableProps;
        if (dataSet) {
            if (isNumber(dataIndex)) {
                return dataSet.get(dataIndex);
            }
            return dataSet.current;
        }
        if (isNumber(dataIndex)) {
            const { contextDataSet } = observableProps;
            if (contextDataSet) {
                return contextDataSet.get(dataIndex);
            }
        }
        return observableProps.contextRecord;
    }
    get dataIndex() {
        const { dataIndex, contextDataIndex } = this.observableProps;
        return defaultTo(dataIndex, contextDataIndex);
    }
    get useColon() {
        const { useColon } = this.observableProps;
        if (useColon !== undefined) {
            return useColon;
        }
        const configUseColon = getConfig('useColon');
        if (configUseColon !== undefined) {
            return configUseColon;
        }
        return false;
    }
    get excludeUseColonTagList() {
        const { excludeUseColonTagList } = this.observableProps;
        if (excludeUseColonTagList !== undefined) {
            return excludeUseColonTagList;
        }
        const configExcludeUseColonTagList = getConfig('excludeUseColonTagList');
        if (configExcludeUseColonTagList !== undefined) {
            return configExcludeUseColonTagList;
        }
        return defaultExcludeUseColonTag;
    }
    get columns() {
        const { columns } = this.observableProps;
        if (isNumber(columns)) {
            return columns;
        }
        if (columns) {
            const responsiveColumns = this.responsiveItems[0];
            if (responsiveColumns) {
                return responsiveColumns;
            }
        }
        return defaultColumns;
    }
    get labelWidth() {
        const { labelWidth } = this.observableProps;
        if (labelWidth === 'auto') {
            return labelWidth;
        }
        if (isNumber(labelWidth) || (isArrayLike(labelWidth) && !hasBreakPointMap(labelWidth))) {
            return labelWidth;
        }
        if (labelWidth) {
            const responsiveWidth = this.responsiveItems[1];
            if (responsiveWidth !== undefined) {
                return responsiveWidth;
            }
        }
        return defaultLabelWidth;
    }
    get labelAlign() {
        const { labelAlign } = this.observableProps;
        const defaultLabelAlign = this.labelLayout === LabelLayout.vertical ? LabelAlign.left : LabelAlign.right;
        if (isString(labelAlign)) {
            return labelAlign;
        }
        if (labelAlign) {
            const responsiveAlign = this.responsiveItems[2];
            if (responsiveAlign) {
                return responsiveAlign;
            }
        }
        return defaultLabelAlign;
    }
    get labelLayout() {
        const { labelLayout } = this.observableProps;
        if (isString(labelLayout)) {
            return labelLayout;
        }
        if (labelLayout) {
            const responsiveLabelLayout = this.responsiveItems[3];
            if (responsiveLabelLayout) {
                return responsiveLabelLayout;
            }
        }
        return getConfig('labelLayout') || LabelLayout.horizontal;
    }
    get labelTooltip() {
        const { labelTooltip } = this.observableProps;
        if (labelTooltip) {
            return labelTooltip;
        }
        return getTooltip('label');
    }
    get readOnly() {
        return this.observableProps.readOnly;
    }
    get pristine() {
        return this.observableProps.pristine;
    }
    get fieldHighlightRenderer() {
        return this.observableProps.fieldHighlightRenderer;
    }
    get showValidation() {
        return this.observableProps.showValidation;
    }
    get separateSpacing() {
        const { separateSpacing } = this.observableProps;
        if (separateSpacing) {
            const { width = 0, height = 0 } = separateSpacing;
            if (width || height) {
                return {
                    width,
                    height,
                };
            }
        }
        return undefined;
    }
    isReadOnly() {
        return this.readOnly;
    }
    getObservableProps(props, context) {
        const observableProps = {
            ...super.getObservableProps(props, context),
            contextDataSet: context.dataSet,
            contextRecord: context.record,
            dataIndex: props.dataIndex,
            contextDataIndex: context.dataIndex,
            labelLayout: 'labelLayout' in props ? props.labelLayout : context.labelLayout,
            labelAlign: 'labelAlign' in props ? props.labelAlign : context.labelAlign,
            showValidation: 'showValidation' in props ? props.showValidation : context.showValidation,
            labelTooltip: 'labelTooltip' in props ? props.labelTooltip : context.labelTooltip,
            disabled: 'disabled' in props ? props.disabled : context.disabled,
            readOnly: 'readOnly' in props ? props.readOnly : context.readOnly,
            labelWidth: defaultTo(props.labelWidth, context.labelWidth),
            pristine: 'pristine' in props ? props.pristine : context.pristine,
            columns: props.columns,
            useColon: props.useColon,
            excludeUseColonTagList: props.excludeUseColonTagList,
            separateSpacing: props.separateSpacing,
            fieldHighlightRenderer: 'fieldHighlightRenderer' in props ? props.fieldHighlightRenderer : context.fieldHighlightRenderer,
        };
        if ('record' in props) {
            observableProps.record = props.record;
        }
        return observableProps;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'record',
            'dataIndex',
            'onSuccess',
            'onError',
            'processParams',
            'labelWidth',
            'labelAlign',
            'labelLayout',
            'labelTooltip',
            'columns',
            'pristine',
            'axios',
            'useColon',
            'excludeUseColonTagList',
            'separateSpacing',
            'fieldHighlightRenderer',
            'layout',
            'showValidation',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        otherProps.onSubmit = this.handleSubmit;
        otherProps.onReset = this.handleReset;
        if (!otherProps.name) {
            otherProps.name = this.name;
        }
        return otherProps;
    }
    getHeader() {
        const { props: { header }, prefixCls, } = this;
        if (header) {
            return (React.createElement("div", { key: "form-header", className: `${prefixCls}-header` }, header));
        }
    }
    getClassName(...props) {
        const { prefixCls, labelLayout } = this;
        return super.getClassName({
            ...props,
            [`${prefixCls}-float-label`]: labelLayout === LabelLayout.float,
        });
    }
    componentWillMount() {
        this.processDataSetListener(true);
    }
    componentDidMount() {
        this.componentDidMountOrUpdate();
        super.componentDidMount();
    }
    componentDidUpdate() {
        this.componentDidMountOrUpdate();
    }
    componentWillUnmount() {
        this.processDataSetListener(false);
    }
    componentDidMountOrUpdate() {
        const { formNode } = this.context;
        const { element } = this;
        if (formNode && element) {
            const isUnderForm = hasParentElement(element.parentElement, 'form');
            if (isUnderForm !== this.isUnderForm) {
                this.isUnderForm = isUnderForm;
            }
        }
    }
    processDataSetListener(flag) {
        const { dataSet } = this;
        if (dataSet) {
            const handler = flag ? dataSet.addEventListener : dataSet.removeEventListener;
            handler.call(dataSet, DataSetEvents.validate, this.handleDataSetValidate);
        }
    }
    // 处理校验失败定位
    async handleDataSetValidate({ result, dataSet }) {
        if (!await result) {
            const [firstInvalidRecord] = dataSet.getValidationErrors();
            if (firstInvalidRecord) {
                const { errors } = firstInvalidRecord;
                if (errors.length) {
                    const [{ field: { name } }] = errors;
                    const field = this.getFields().find(item => item.props.name === name);
                    if (field) {
                        field.focus();
                    }
                }
            }
        }
    }
    rasterizedChildren() {
        const { dataSet, record, columns, labelLayout, labelAlign, labelTooltip, useColon, excludeUseColonTagList, readOnly: formReadOnly, props: { children }, } = this;
        const prefixCls = getProPrefixCls(FIELD_SUFFIX);
        const labelWidth = normalizeLabelWidth(this.labelWidth, columns);
        const rows = [];
        let cols = [];
        let rowIndex = 0;
        let colIndex = 0;
        const matrix = [[]];
        let noLabel = true;
        const childrenArray = [];
        const separateSpacingWidth = this.separateSpacing ? this.separateSpacing.width / 2 : 0;
        Children.forEach(children, (child) => {
            if (isValidElement(child)) {
                const setChild = (arr, outChild, groupProps = {}) => {
                    const { type, props: outChildProps } = outChild;
                    if (outChildProps.hidden)
                        return null;
                    if (type) {
                        if (noLabel === true &&
                            labelLayout === LabelLayout.horizontal &&
                            getProperty(outChildProps, 'label', dataSet, record)) {
                            noLabel = false;
                        }
                        const { children: outChildChildren, ...otherOutChildProps } = outChildProps;
                        const isFragmentElement = isFragment(outChild);
                        if (isFragmentElement || type.__PRO_FORM_VIRTUAL_GROUP) {
                            Children.forEach(outChildChildren, (c) => {
                                if (isValidElement(c)) {
                                    setChild(arr, c, isFragmentElement ? groupProps : { ...groupProps, ...otherOutChildProps });
                                }
                            });
                        }
                        else {
                            arr.push(cloneElement(outChild, {
                                ...groupProps,
                                ...outChildProps,
                            }));
                        }
                    }
                };
                setChild(childrenArray, child);
            }
        });
        function completeLine() {
            if (cols.length) {
                rows.push((React.createElement("tr", { key: `row-${rowIndex}` }, cols)));
                cols = [];
            }
            rowIndex++;
            colIndex = 0;
            matrix[rowIndex] = matrix[rowIndex] || [];
        }
        for (let index = 0, len = childrenArray.length; index < len;) {
            const { props, key, type, ref } = childrenArray[index];
            const TagName = isFunction(type) ? type.displayName : type;
            const label = getProperty(props, 'label', dataSet, record);
            const fieldLabelWidth = getProperty(props, 'labelWidth', dataSet, record);
            const required = getProperty(props, 'required', dataSet, record);
            const readOnly = getProperty(props, 'readOnly', dataSet, record) || formReadOnly;
            const { rowSpan = 1, colSpan = 1, newLine, className, fieldClassName, useColon: fieldUseColon = useColon, ...otherProps } = props;
            let newColSpan = colSpan;
            const currentRow = matrix[rowIndex];
            if (newLine) {
                if (colIndex !== 0) {
                    completeLine();
                    continue;
                }
            }
            while (currentRow[colIndex]) {
                colIndex++;
            }
            if (colIndex >= columns) {
                completeLine();
                continue;
            }
            if (newColSpan + colIndex > columns) {
                newColSpan = columns - colIndex;
            }
            for (let i = colIndex, k = colIndex + newColSpan; i < k; i++) {
                if (currentRow[i]) {
                    newColSpan = i - colIndex;
                    break;
                }
            }
            for (let i = rowIndex; i < rowSpan + rowIndex; i++) {
                for (let j = colIndex, k = newColSpan + colIndex; j < k; j++) {
                    if (!matrix[i]) {
                        matrix[i] = [];
                    }
                    matrix[i][j] = true;
                }
            }
            const isOutput = labelLayout === LabelLayout.horizontal && type.displayName === 'Output';
            const labelClassName = classNames(`${prefixCls}-label`, `${prefixCls}-label-${labelAlign}`, fieldClassName, {
                [`${prefixCls}-required`]: required,
                [`${prefixCls}-readonly`]: readOnly,
                [`${prefixCls}-label-vertical`]: labelLayout === LabelLayout.vertical,
                [`${prefixCls}-label-output`]: isOutput,
                [`${prefixCls}-label-useColon`]: label && fieldUseColon && !excludeUseColonTagList.find(v => v === TagName),
            });
            const wrapperClassName = classNames(`${prefixCls}-wrapper`, {
                [`${prefixCls}-output`]: isOutput,
            });
            if (!noLabel && !type.__PRO_FORM_ITEM) {
                const columnLabelWidth = labelWidth[colIndex];
                if (!isNaN(fieldLabelWidth) && columnLabelWidth !== 'auto') {
                    labelWidth[colIndex] = Math.max(columnLabelWidth, fieldLabelWidth);
                }
                const tooltip = 'labelTooltip' in props ? props.labelTooltip : labelTooltip;
                cols.push(React.createElement(FormItemLabel, { key: `row-${rowIndex}-col-${colIndex}-label`, className: labelClassName, rowSpan: rowSpan, paddingLeft: this.labelLayout === LabelLayout.horizontal && separateSpacingWidth ? pxToRem(separateSpacingWidth + 5) : undefined, tooltip: tooltip }, label));
            }
            const fieldElementProps = {
                ref,
                key,
                className: classNames(prefixCls, className),
                ...otherProps,
            };
            if (!isString(type)) {
                fieldElementProps.rowIndex = rowIndex;
                fieldElementProps.colIndex = colIndex;
            }
            cols.push(React.createElement("td", { key: `row-${rowIndex}-col-${colIndex}-field`, colSpan: noLabel ? newColSpan : newColSpan * 2 - (type.__PRO_FORM_ITEM ? 0 : 1), rowSpan: rowSpan, className: fieldClassName, style: this.labelLayout === LabelLayout.horizontal
                    && separateSpacingWidth
                    ? { paddingRight: pxToRem(separateSpacingWidth + 5) } : undefined },
                labelLayout === LabelLayout.vertical && !!label && (React.createElement("label", { className: labelClassName }, label)),
                React.createElement("div", { className: wrapperClassName }, createElement(type, fieldElementProps))));
            if (index === len - 1) {
                completeLine();
            }
            else {
                colIndex++;
            }
            index++;
        }
        cols = [];
        const isAutoWidth = !noLabel && labelWidth.some(w => w === 'auto');
        if (!noLabel) {
            for (let i = 0; i < columns; i++) {
                const key = `label-${i}`;
                const columnLabelWidth = labelWidth[i % columns];
                if (columnLabelWidth === 'auto') {
                    cols.push(React.createElement("col", { key: key }));
                }
                else {
                    cols.push(React.createElement("col", { key: key, 
                        // 优化当使用separateSoacing label宽度太窄问题
                        style: { width: pxToRem(labelLayout === LabelLayout.horizontal ? separateSpacingWidth + columnLabelWidth : columnLabelWidth) } }));
                }
                cols.push(React.createElement("col", { key: `wrapper-${i}`, style: isAutoWidth ? { width: `${100 / columns}%` } : undefined }));
            }
        }
        else {
            for (let i = 0; i < columns; i++) {
                cols.push(React.createElement("col", { key: `wrapper-${i}` }));
            }
        }
        let tableStyle;
        const { separateSpacing } = this;
        if (separateSpacing) {
            if (labelLayout === LabelLayout.horizontal) {
                tableStyle = {
                    borderCollapse: 'separate',
                    borderSpacing: `0rem ${pxToRem(separateSpacing.height)}`,
                };
            }
            else {
                tableStyle = {
                    borderCollapse: 'separate',
                    borderSpacing: `${pxToRem(separateSpacing.width)} ${pxToRem(separateSpacing.height)}`,
                };
            }
        }
        return (React.createElement("table", { key: "form-body", style: tableStyle, className: `${isAutoWidth ? 'auto-width' : ''}` },
            cols.length ? React.createElement("colgroup", null, cols) : undefined,
            React.createElement("tbody", null, rows)));
    }
    render() {
        const { labelWidth, labelAlign, labelLayout, labelTooltip, pristine, dataSet, record, dataIndex, observableProps, disabled, readOnly, fieldHighlightRenderer, props, useColon, showValidation, } = this;
        const { formNode } = this.context;
        const value = {
            formNode: formNode || this,
            dataSet,
            dataIndex,
            record,
            labelWidth,
            labelAlign,
            labelLayout,
            labelTooltip,
            pristine,
            disabled,
            readOnly,
            fieldHighlightRenderer,
            useColon,
            showValidation,
        };
        const children = [
            this.getHeader(),
            props.layout === FormLayout.table ? this.rasterizedChildren() : props.children,
        ];
        return (React.createElement(Responsive, { items: [
                observableProps.columns,
                observableProps.labelWidth,
                observableProps.labelAlign,
                observableProps.labelLayout,
            ], onChange: this.handleResponsive },
            React.createElement(FormContext.Provider, { value: value }, this.isUnderForm ? children :
                formNode && this.isUnderForm === undefined ? (React.createElement("div", Object.assign({}, this.getMergedProps()), children)) : (React.createElement("form", Object.assign({}, this.getMergedProps(), { noValidate: true }), children)))));
    }
    handleResponsive(items) {
        this.responsiveItems = items;
    }
    async handleSubmit(e) {
        e.preventDefault();
        e.persist();
        if (await this.checkValidity()) {
            const { target, action, dataSet, method, processParams = noop, onSuccess = noop, onError = noop, onSubmit = noop, } = this.props;
            onSubmit(e);
            try {
                if (dataSet) {
                    onSuccess(await dataSet.submit());
                }
                else if (action) {
                    if (target && this.element) {
                        this.element.submit();
                    }
                    else {
                        onSuccess(await this.axios[method || 'get'](action, processParams(e)));
                    }
                }
            }
            catch (error) {
                onError(error);
            }
        }
    }
    handleReset(e) {
        const { onReset = noop } = this.props;
        const { record } = this;
        onReset(e);
        if (!e.isDefaultPrevented()) {
            if (record) {
                record.reset();
            }
            else {
                this.getFields().forEach(field => field.reset());
            }
        }
    }
    reportValidity(result) {
        const { prepareForReport } = this;
        if (!result) {
            prepareForReport.result = result;
        }
        if (prepareForReport.timeout) {
            window.clearTimeout(prepareForReport.timeout);
        }
        prepareForReport.timeout = window.setTimeout(() => {
            if (!prepareForReport.result) {
                const field = this.getFields().find(one => !one.isValid);
                if (field) {
                    field.focus();
                }
            }
            this.prepareForReport = {};
        }, 200);
    }
    checkValidity() {
        const { dataSet } = this;
        if (dataSet) {
            if (!dataSet.length) {
                dataSet.create();
            }
            return dataSet.validate();
        }
        this.validating = true;
        return Promise.all(this.getFields().map(field => field.checkValidity())).then((results) => {
            const valid = results.every(result => result);
            this.reportValidity(valid);
            this.validating = false;
            return valid;
        }).catch((error) => {
            this.validating = false;
            throw error;
        });
    }
    getFields() {
        const { id } = this.props;
        if (id) {
            return [].concat(this.fields, getFieldsById(id));
        }
        return this.fields;
    }
    getField(name) {
        return this.getFields().find(field => field.props.name === name);
    }
    addField(field) {
        this.fields.push(field);
    }
    removeField(field) {
        const index = this.fields.indexOf(field);
        if (index !== -1) {
            this.fields.splice(index, 1);
        }
    }
};
Form.displayName = 'Form';
Form.FormVirtualGroup = FormVirtualGroup;
Form.Item = Item;
Form.propTypes = {
    /**
     * 表单提交请求地址
     */
    action: PropTypes.string,
    /**
     * 表单提交的HTTP Method
     * 可选值：POST | GET
     * @default POST
     */
    method: PropTypes.string,
    /**
     * 表单提交的目标
     * 当表单设置了设置target且没有dataSet时作浏览器默认提交，否则作Ajax提交
     */
    target: PropTypes.string,
    /**
     * Ajax提交时的参数回调
     */
    processParams: PropTypes.func,
    /**
     * 只读
     */
    readOnly: PropTypes.bool,
    /**
     * 内部控件的标签的宽度
     */
    labelWidth: PropTypes.oneOfType([
        labelWidthPropTypes,
        PropTypes.shape({
            [ResponsiveKeys.xs]: labelWidthPropTypes,
            [ResponsiveKeys.sm]: labelWidthPropTypes,
            [ResponsiveKeys.md]: labelWidthPropTypes,
            [ResponsiveKeys.lg]: labelWidthPropTypes,
            [ResponsiveKeys.xl]: labelWidthPropTypes,
            [ResponsiveKeys.xxl]: labelWidthPropTypes,
        }),
    ]),
    useColon: PropTypes.bool,
    excludeUseColonTagList: PropTypes.array,
    /**
     * 标签文字对齐方式
     * 可选值： 'left' | 'center' | 'right'
     */
    labelAlign: PropTypes.oneOfType([
        labelAlignPropTypes,
        PropTypes.shape({
            [ResponsiveKeys.xs]: labelAlignPropTypes,
            [ResponsiveKeys.sm]: labelAlignPropTypes,
            [ResponsiveKeys.md]: labelAlignPropTypes,
            [ResponsiveKeys.lg]: labelAlignPropTypes,
            [ResponsiveKeys.xl]: labelAlignPropTypes,
            [ResponsiveKeys.xxl]: labelAlignPropTypes,
        }),
    ]),
    /**
     * 标签位置
     * 可选值： 'horizontal' | 'vertical' | 'placeholder' | 'float' | 'none'
     */
    labelLayout: PropTypes.oneOfType([
        labelLayoutPropTypes,
        PropTypes.shape({
            [ResponsiveKeys.xs]: labelLayoutPropTypes,
            [ResponsiveKeys.sm]: labelLayoutPropTypes,
            [ResponsiveKeys.md]: labelLayoutPropTypes,
            [ResponsiveKeys.lg]: labelLayoutPropTypes,
            [ResponsiveKeys.xl]: labelLayoutPropTypes,
            [ResponsiveKeys.xxl]: labelLayoutPropTypes,
        }),
    ]),
    /**
     * 表单列数
     */
    columns: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            [ResponsiveKeys.xs]: PropTypes.number,
            [ResponsiveKeys.sm]: PropTypes.number,
            [ResponsiveKeys.md]: PropTypes.number,
            [ResponsiveKeys.lg]: PropTypes.number,
            [ResponsiveKeys.xl]: PropTypes.number,
            [ResponsiveKeys.xxl]: PropTypes.number,
        }),
    ]),
    pristine: PropTypes.bool,
    /**
     * 表单头
     */
    header: PropTypes.string,
    /**
     * 高亮渲染器
     */
    fieldHighlightRenderer: PropTypes.func,
    /**
     * 提交回调
     */
    onSubmit: PropTypes.func,
    /**
     * 重置回调
     */
    onReset: PropTypes.func,
    /**
     * 提交成功回调
     */
    onSuccess: PropTypes.func,
    /**
     * 提交失败回调
     */
    onError: PropTypes.func,
    separateSpacing: PropTypes.object,
    /**
     * 校验信息提示方式
     */
    showValidation: PropTypes.string,
    ...DataSetComponent.propTypes,
};
Form.defaultProps = {
    suffixCls: 'form',
    columns: defaultColumns,
    labelWidth: defaultLabelWidth,
    layout: FormLayout.table,
};
Form.contextType = FormContext;
__decorate([
    observable
], Form.prototype, "responsiveItems", void 0);
__decorate([
    observable
], Form.prototype, "isUnderForm", void 0);
__decorate([
    computed
], Form.prototype, "axios", null);
__decorate([
    computed
], Form.prototype, "dataSet", null);
__decorate([
    computed
], Form.prototype, "record", null);
__decorate([
    computed
], Form.prototype, "dataIndex", null);
__decorate([
    computed
], Form.prototype, "useColon", null);
__decorate([
    computed
], Form.prototype, "excludeUseColonTagList", null);
__decorate([
    computed
], Form.prototype, "columns", null);
__decorate([
    computed
], Form.prototype, "labelWidth", null);
__decorate([
    computed
], Form.prototype, "labelAlign", null);
__decorate([
    computed
], Form.prototype, "labelLayout", null);
__decorate([
    computed
], Form.prototype, "labelTooltip", null);
__decorate([
    computed
], Form.prototype, "readOnly", null);
__decorate([
    computed
], Form.prototype, "pristine", null);
__decorate([
    computed
], Form.prototype, "fieldHighlightRenderer", null);
__decorate([
    computed
], Form.prototype, "showValidation", null);
__decorate([
    computed
], Form.prototype, "separateSpacing", null);
__decorate([
    mobxAction
], Form.prototype, "componentDidMountOrUpdate", null);
__decorate([
    autobind
], Form.prototype, "handleDataSetValidate", null);
__decorate([
    autobind,
    mobxAction
], Form.prototype, "handleResponsive", null);
__decorate([
    autobind
], Form.prototype, "handleSubmit", null);
__decorate([
    autobind
], Form.prototype, "handleReset", null);
Form = __decorate([
    observer
], Form);
export default Form;
